import { useQuery } from "@tanstack/react-query";
import { useMatches } from "@tanstack/react-router";
import { pageDatesQuery } from "@/lib/page-freshness.functions";
import {
  BADGE_LABELS,
  formatBadgeDate,
  hasOwnDateSource,
  routeKeyFromMatches,
  type PageDates,
} from "@/lib/seo/page-freshness";

/**
 * Truthful dates for the current page, from the persisted `page_freshness`
 * record (preloaded by the root loader). Missing evidence → null, never a guess.
 */
export function usePageDates(): PageDates {
  const matches = useMatches();
  const key = routeKeyFromMatches(matches);
  const { data } = useQuery(pageDatesQuery);
  if (!key || hasOwnDateSource(key)) return { published: null, reviewed: null };
  return data?.[key] ?? { published: null, reviewed: null };
}

/**
 * The single public "First published / Last reviewed" badge. Every template
 * renders page dates through this component so they cannot drift.
 */
export function PageDatesBadge({
  lang,
  className = "",
  only,
}: {
  lang?: "en" | "es";
  className?: string;
  only?: "reviewed" | "published";
}) {
  const { published, reviewed } = usePageDates();
  const matches = useMatches();
  const path = matches[matches.length - 1]?.pathname ?? "/";
  const l: "en" | "es" = lang ?? (/^\/es(\/|$)/.test(path) ? "es" : "en");
  const labels = BADGE_LABELS[l];
  const showPub = published && only !== "reviewed";
  const showRev = reviewed && only !== "published";
  if (!showPub && !showRev) return null;
  return (
    <div className={`flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground ${className}`} data-page-dates>
      {showPub ? (
        <span>
          <span className="uppercase tracking-wide">{labels.published} </span>
          <time className="font-semibold text-foreground" dateTime={published!}>
            {formatBadgeDate(published!, l)}
          </time>
        </span>
      ) : null}
      {showRev ? (
        <span>
          <span className="uppercase tracking-wide">{labels.reviewed} </span>
          <time className="font-semibold text-foreground" dateTime={reviewed!}>
            {formatBadgeDate(reviewed!, l)}
          </time>
        </span>
      ) : null}
    </div>
  );
}
